import { doLegalNamesMatch } from "src/views/hacker/rsvp/confirmation/signature";

export const BADGE_ELIGIBLE_ROLES = [
  "organizer",
  "hacker",
  "sponsor",
  "judge",
] as const;

export const APP_MODE_LABELS = Object.freeze({
  default: "Default apps only",
  public: "All public apps",
  all: "Public + diagnostics",
});

export type BadgeAppMode = keyof typeof APP_MODE_LABELS;

export type RegistrationWaiverStatus = {
  signed: boolean;
  signedAt: string | null;
  signer: "adult" | "minor" | null;
};

export const registrationWaiverStatus = ({
  legalName,
  signature,
  uploadedFiles,
}: {
  legalName?: string | null;
  signature?: string | null;
  uploadedFiles?: unknown[] | null;
}): RegistrationWaiverStatus => {
  const hasUploadedWaiver = Boolean(uploadedFiles?.length);
  if (hasUploadedWaiver)
    return { signed: true, signedAt: null, signer: "minor" };
  if (doLegalNamesMatch(signature, legalName)) {
    return { signed: true, signedAt: null, signer: "adult" };
  }
  return { signed: false, signedAt: null, signer: null };
};

export const normalizedClaimStage = (stage?: string | null): string =>
  (stage || "").trim().toLowerCase().replace(/-/g, "_");

export const registrationStageIsEligible = (stage?: string | null): boolean =>
  ["confirmed", "checked_in"].includes(normalizedClaimStage(stage));

export const extractBadgeId = (value: string): string => {
  const path = value.trim().split(/[?#]/)[0].replace(/\/+$/, "");
  if (!path) return "";

  const lastSegment = path.slice(path.lastIndexOf("/") + 1);
  try {
    return decodeURIComponent(lastSegment).trim();
  } catch {
    return lastSegment.trim();
  }
};

const normalizeRole = (role: string): string =>
  role
    .trim()
    .toLowerCase()
    .replace(/[\s/-]+/g, "_");

export const badgeAppModeForRole = (role: string): BadgeAppMode => {
  const normalized = normalizeRole(role);
  if (normalized === "organizer") return "all";
  return normalized === "hacker" ? "default" : "public";
};

export type BadgeIdentity = {
  version: 1;
  badge_id: string;
  attendee_id: number;
  role: string;
  display_name: string;
  account_email: string;
  provisioned_unix: number;
  claim_id?: string;
  net_email?: string;
  net_phone?: string;
  net_linkedin?: string;
  net_discord?: string;
  net_instagram?: string;
  net_x?: string;
};

export type BadgeProfile = {
  claimId?: number | null;
  badgeDisplayName?: string | null;
  networkingEmail?: string | null;
  networkingPhone?: string | null;
  networkingLinkedIn?: string | null;
  networkingDiscord?: string | null;
  networkingInstagram?: string | null;
  networkingX?: string | null;
};

type BadgeAttendee = {
  eventAttendeeId: number;
  role: string;
  registrationData: {
    preferredName?: string | null;
    legalName?: string | null;
  };
};

const BADGE_ID_WORDS = `
acorn alpaca anchor apple arrow badger bamboo beacon bear birch bison blossom bluejay brook cactus
cedar cherry cloud comet coral crane cricket crown daisy dawn deer dolphin dove dragon eagle ember
falcon fern finch firefly forest fox frost garden gecko glade goose grove harbor hawk hazel heron
hill honey ibis island ivy jade jay koala lake lantern lark leaf lemur lilac lion lotus lynx maple
marlin meadow meteor mink moon moose moss nebula newt oak ocean olive orchid otter owl panda pearl
penguin petal pine poppy puffin quartz rabbit raven reef river robin rocket rose sage seal shark
shell sparrow spruce star stone storm sun swan swift thistle thunder tiger toucan trout tulip
turtle valley velvet violet whale willow wolf wren yarrow zebra zenith zephyr summit ripple canyon
agile air almond antler aspen atlas aurora autumn bay beach berry bloom breeze brick bronze bubble
cabin camel candle candy castle cave clover cocoa coffee copper creek delta dream dune dusk echo
elm fable feather field flame flint flower flute gem ginger glacier glow grape grass gull hickory
horizon horse juniper kite kiwi lagoon lemon light lime magpie mango marble melon mist morning moth
nectar night north oasis opal orbit palm pebble pepper phoenix planet plum prism rain reed ruby
salmon sand satin scarlet seed shadow sky snow sonnet south sunset thorn tide topaz trail wave west
wheat wind wing winter wood amber aqua azure blue bold brave bright calm cosmic crisp eager gentle
golden happy hazy indigo jolly lucky lunar mellow mint noble peach quiet rapid vivid
`
  .trim()
  .split(/\s+/);

export const preferredEventAttendee = <T extends { role: string }>(
  attendees: T[]
): T | undefined =>
  attendees.find(({ role }) => normalizeRole(role) === "organizer") ||
  attendees[0];

export const attendeeReceivesBadge = (role: string): boolean =>
  (BADGE_ELIGIBLE_ROLES as readonly string[]).includes(normalizeRole(role));

export const toBadgeRole = (role: string): string => {
  const normalized = normalizeRole(role);
  const supportedRoles = [
    "organizer",
    "sponsor",
    "general",
    "workshop_lead",
    "mentor",
    "judge",
    "volunteer",
    "hacker",
    "visitor",
  ];
  return supportedRoles.includes(normalized) ? normalized : "general";
};

const trimOrUndefined = (
  value: string | null | undefined
): string | undefined => value?.trim() || undefined;

const socialHandle = (value: string | null | undefined): string | undefined => {
  const trimmed = trimOrUndefined(value);
  if (!trimmed) return undefined;
  if (!trimmed.includes("/")) return trimmed.replace(/^@/, "");

  try {
    const url = new URL(
      /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    );
    return url.pathname.split("/").filter(Boolean).pop()?.replace(/^@/, "");
  } catch {
    return trimmed.replace(/^@/, "").split("/").filter(Boolean).pop();
  }
};

export const attendeeDisplayName = (
  attendee: Pick<BadgeAttendee, "registrationData">
): string =>
  trimOrUndefined(attendee.registrationData.preferredName) ||
  trimOrUndefined(attendee.registrationData.legalName) ||
  "Attendee";

export const buildBadgeIdentity = ({
  attendee,
  profile,
  accountEmail,
  badgeId,
  now = Date.now(),
}: {
  attendee: BadgeAttendee;
  profile: BadgeProfile;
  accountEmail: string;
  badgeId: string;
  now?: number;
}): BadgeIdentity => {
  const identity: BadgeIdentity = {
    version: 1,
    badge_id: badgeId,
    attendee_id: attendee.eventAttendeeId,
    role: toBadgeRole(attendee.role),
    display_name:
      trimOrUndefined(profile.badgeDisplayName) ||
      attendeeDisplayName(attendee),
    account_email: accountEmail.trim(),
    provisioned_unix: Math.floor(now / 1000),
  };

  const optionalValues: Partial<BadgeIdentity> = {
    claim_id: profile.claimId == null ? undefined : String(profile.claimId),
    net_email: trimOrUndefined(profile.networkingEmail),
    net_phone: trimOrUndefined(profile.networkingPhone),
    net_linkedin: socialHandle(profile.networkingLinkedIn),
    net_discord: trimOrUndefined(profile.networkingDiscord),
    net_instagram: socialHandle(profile.networkingInstagram),
    net_x: socialHandle(profile.networkingX),
  };

  Object.entries(optionalValues).forEach(([key, value]) => {
    if (value) {
      (identity as Record<string, string | number>)[key] = value;
    }
  });

  return identity;
};

export const generateBadgeId = (
  virtual = false,
  randomBytes?: Uint8Array
): string => {
  const randomWordCount = virtual ? 3 : 4;
  const bytes = randomBytes ?? new Uint8Array(randomWordCount);
  if (!randomBytes) window.crypto.getRandomValues(bytes);
  if (bytes.length < randomWordCount) {
    throw new Error("Not enough randomness to generate a badge ID");
  }

  const words = Array.from(bytes.slice(0, randomWordCount)).map(
    (index) => BADGE_ID_WORDS[index]
  );
  return virtual ? ["virtual", ...words].join("-") : words.join("-");
};

export class BadgeAllocationError extends Error {
  constructor() {
    super("Could not allocate a unique badge ID after 5 attempts.");
    this.name = "BadgeAllocationError";
  }
}

export const allocateBadgeId = async ({
  virtual,
  register,
  generate = generateBadgeId,
}: {
  virtual: boolean;
  register: (badgeId: string) => Promise<unknown>;
  generate?: (virtual: boolean) => string;
}): Promise<string> => {
  for (let attempt = 0; attempt < 5; attempt++) {
    const badgeId = generate(virtual);
    try {
      await register(badgeId);
      return badgeId;
    } catch {
      // The registration mutation owns the uniqueness constraint. Generate a
      // fresh ID and retry collisions (or transient failures) up to the desk's
      // explicit five-attempt limit.
    }
  }
  throw new BadgeAllocationError();
};
